import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { CalculatorInput, CalculatorResult, CalculatorResultsGrid } from '@/components/calculators'
import {
  calculateMonthlyPayment,
  calculateCashOnCash,
  calculateMonthlyCashFlow,
  formatCurrency,
  formatPercent,
} from '@/lib/calculators/formulas'

export default function BrrrrCalculator() {
  // Buy
  const [purchasePrice, setPurchasePrice] = useState(150000)
  const [purchaseDownPercent, setPurchaseDownPercent] = useState(25)
  const [closingCosts, setClosingCosts] = useState(4000)

  // Rehab
  const [rehabCosts, setRehabCosts] = useState(30000)

  // Rent
  const [monthlyRent, setMonthlyRent] = useState(2200)
  const [vacancyRate, setVacancyRate] = useState(8)
  const [monthlyExpenses, setMonthlyExpenses] = useState(450)

  // Refinance
  const [arv, setArv] = useState(220000)
  const [refiLTV, setRefiLTV] = useState(75)
  const [refiRate, setRefiRate] = useState(7)
  const [refiTerm, setRefiTerm] = useState(30)

  // Calculations
  const purchaseDown = purchasePrice * (purchaseDownPercent / 100)
  const totalCashIn = purchaseDown + closingCosts + rehabCosts
  const allInCost = purchasePrice + rehabCosts + closingCosts

  // Refinance
  const newLoanAmount = arv * (refiLTV / 100)
  const cashOutFromRefi = newLoanAmount - (purchasePrice - purchaseDown) // pays off original loan, rest is cash out
  const cashLeftInDeal = Math.max(0, totalCashIn - cashOutFromRefi)
  const cashRecovered = Math.min(totalCashIn, cashOutFromRefi)
  const recoveryPercent = (cashRecovered / totalCashIn) * 100

  // Post-refi cash flow
  const monthlyDebtService = calculateMonthlyPayment(newLoanAmount, refiRate, refiTerm)
  const monthlyCashFlow = calculateMonthlyCashFlow(monthlyRent, vacancyRate, monthlyExpenses, monthlyDebtService)
  const annualCashFlow = monthlyCashFlow * 12

  // Cash-on-cash (based on cash LEFT in deal)
  const cashOnCash =
    cashLeftInDeal > 0 ? calculateCashOnCash(annualCashFlow, cashLeftInDeal) : annualCashFlow > 0 ? Infinity : 0

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 py-12">
      <Link to="/resources/calculators" className="text-amber-600 hover:underline">
        &larr; Back to Calculators
      </Link>
      <h1 className="text-3xl font-bold">BRRRR Strategy Calculator</h1>

      <Card>
        <CardContent className="p-6">
          <p>
            <strong>Buy, Rehab, Rent, Refinance, Repeat.</strong> The BRRRR strategy lets you recycle capital by forcing
            appreciation through renovation, then refinancing based on the improved value. The goal: recover most or all
            of your initial cash while keeping the property.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Buy</h2>

            <CalculatorInput label="Purchase Price" value={purchasePrice} onChange={setPurchasePrice} prefix="$" />

            <CalculatorInput
              label="Down Payment"
              value={purchaseDownPercent}
              onChange={setPurchaseDownPercent}
              suffix="%"
            />

            <CalculatorInput label="Closing Costs" value={closingCosts} onChange={setClosingCosts} prefix="$" />

            <Separator />

            <h2 className="text-lg font-semibold">Rehab</h2>

            <CalculatorInput label="Rehab Budget" value={rehabCosts} onChange={setRehabCosts} prefix="$" />

            <div className="text-muted-foreground text-sm">
              All-in cost: {formatCurrency(allInCost)} | Cash invested: {formatCurrency(totalCashIn)}
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Rent</h2>

            <CalculatorInput label="Monthly Gross Rent" value={monthlyRent} onChange={setMonthlyRent} prefix="$" />

            <CalculatorInput label="Vacancy Rate" value={vacancyRate} onChange={setVacancyRate} suffix="%" />

            <CalculatorInput
              label="Monthly Operating Expenses"
              value={monthlyExpenses}
              onChange={setMonthlyExpenses}
              prefix="$"
            />

            <Separator />

            <h2 className="text-lg font-semibold">Refinance</h2>

            <CalculatorInput label="After-Repair Value (ARV)" value={arv} onChange={setArv} prefix="$" />

            <CalculatorInput
              label="Refinance LTV"
              value={refiLTV}
              onChange={setRefiLTV}
              suffix="%"
              tooltip="Most lenders cap at 75% LTV for investment properties"
            />

            <CalculatorInput label="Refinance Rate" value={refiRate} onChange={setRefiRate} suffix="%" />

            <div className="space-y-1.5">
              <span className="text-sm font-medium">Loan Term</span>
              <div className="flex gap-2" role="group" aria-label="Loan Term">
                {[15, 20, 30].map((term) => (
                  <button
                    key={term}
                    onClick={() => setRefiTerm(term)}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      refiTerm === term ? 'bg-amber-500 text-white' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {term} years
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Capital Recovery</h2>

            <CalculatorResult
              label="Cash Recovered"
              value={formatPercent(Math.min(recoveryPercent, 100))}
              highlight
              subtext={`${formatCurrency(cashRecovered)} of ${formatCurrency(totalCashIn)} returned at refinance`}
            />

            <CalculatorResultsGrid>
              <CalculatorResult label="Cash Left in Deal" value={formatCurrency(cashLeftInDeal)} />
              <CalculatorResult label="New Loan Amount" value={formatCurrency(newLoanAmount)} />
              <CalculatorResult label="Equity After Refi" value={formatCurrency(arv - newLoanAmount)} />
              <CalculatorResult
                label="Forced Appreciation"
                value={formatCurrency(arv - purchasePrice)}
                subtext="ARV - Purchase Price"
              />
            </CalculatorResultsGrid>

            <Separator />

            <h2 className="text-lg font-semibold">Post-Refinance Cash Flow</h2>

            <CalculatorResultsGrid>
              <CalculatorResult label="Monthly Cash Flow" value={formatCurrency(monthlyCashFlow)} />
              <CalculatorResult label="Annual Cash Flow" value={formatCurrency(annualCashFlow)} />
              <CalculatorResult label="Monthly Debt Service" value={formatCurrency(monthlyDebtService)} />
              <CalculatorResult
                label="Cash-on-Cash Return"
                value={cashOnCash === Infinity ? 'Infinite' : formatPercent(cashOnCash)}
                subtext={
                  cashLeftInDeal === 0 ? 'No cash left in deal' : `On ${formatCurrency(cashLeftInDeal)} remaining`
                }
              />
            </CalculatorResultsGrid>

            <div className="bg-muted/50 text-muted-foreground space-y-1 rounded-lg p-3 text-sm">
              <p>
                <strong>BRRRR Success Criteria:</strong>
              </p>
              <p>75%+ capital recovery = Good</p>
              <p>90%+ capital recovery = Excellent</p>
              <p>100% recovery = Full recycle (repeat with same capital)</p>
              <p>Positive cash flow after refi = Sustainable hold</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
